'use client';

import React, { Children, forwardRef, useId } from 'react';
import BaseSwiper from '@/components/Swiper/BaseSwiper';

const defaultBreakpoints = {
  768: {
    slidesPerView: 2,
    spaceBetween: 16
  },
  1024: {
    slidesPerView: 3,
    spaceBetween: 40
  }
};
const defaultSpaceBetween = 20;
const defaultSlidesPerView = 1;
const defaultAutoHeight = false;
const defaultPagination = { clickable: true };

const Swiper = forwardRef(
  (
    {
      children,
      breakpoints = defaultBreakpoints,
      spaceBetween = defaultSpaceBetween,
      slidesPerView = defaultSlidesPerView,
      autoHeight = defaultAutoHeight,
      className = '',
      pagination = defaultPagination,
      fitMinimumSlidesPerView = false,
      ...rest
    },
    ref
  ) => {
    const id = 'swiper-' + useId().replace(/:/g, '');
    const _breakpoints = { 0: { slidesPerView: slidesPerView ?? 1 }, ...breakpoints };

    const childrenLength = Children.count(children);

    if (fitMinimumSlidesPerView) {
      Object.entries(_breakpoints).forEach(([key, value]) => {
        _breakpoints[key] = {
          ...value,
          slidesPerView: Math.min(value.slidesPerView, childrenLength)
        };
      });
    }

    const hidePagination = typeof pagination === 'boolean' && pagination === false;

    const _className = Object.entries(_breakpoints)
      .map(([key, value]) => {
        const { slidesPerView = 1, spaceBetween: gap = spaceBetween } = value;
        return `
      @media (min-width: ${key}px) {
        #${id} .swiper-wrapper:nth-of-type(1) {
          padding-bottom: ${hidePagination || slidesPerView >= childrenLength ? 0 : `var(--swiper-padding-bottom, min(var(--section-py) * 2.5, ${60 + childrenLength / slidesPerView}px))`}
        }
        .${id}-slide {
          width: calc((100% - ${(slidesPerView - 1) * gap}px) / ${slidesPerView});
          margin-right: ${gap}px;
        }            
      }`;
      })
      .join(' ')
      .replace(/\s+/g, ' ');

    return (
      <>
        <style>{_className}</style>
        <BaseSwiper
          breakpoints={_breakpoints}
          spaceBetween={spaceBetween}
          slidesPerView={slidesPerView}
          autoHeight={autoHeight}
          className={className}
          pagination={pagination}
          id={id}
          ref={ref}
          loop
          {...rest}
        >
          {children}
        </BaseSwiper>
      </>
    );
  }
);
Swiper.displayName = 'Swiper';

export default Swiper;
